import React, { useState, useRef } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://myreadyroom-api-crchhghhctcvgagq.westus2-01.azurewebsites.net';

interface FileUploadProps {
  onExtracted: (text: string) => void;
  fieldName: string;
}

export default function FileUpload({ onExtracted, fieldName }: FileUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [redactedText, setRedactedText] = useState('');
  const [redactedCount, setRedactedCount] = useState(0);
  const [filename, setFilename] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setShowPreview(false);
    setConfirmed(false);
    setFilename(file.name);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('redact', 'true');

      const response = await axios.post(
        `${API_BASE_URL}/api/upload/extract`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setRedactedText(response.data.text);
      setRedactedCount(response.data.redactedCount);
      setShowPreview(true);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to process file. Please try copy/paste instead.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleConfirm = () => {
    if (!confirmed) {
      alert('Please check the confirmation box first.');
      return;
    }
    onExtracted(redactedText);
    setShowPreview(false);
    setRedactedText('');
    setFilename('');
  };

  const handleDiscard = () => {
    setShowPreview(false);
    setRedactedText('');
    setFilename('');
    setConfirmed(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.uploadRow}>
        <button
          type="button"
          style={styles.uploadButton}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Processing...' : `↑ Upload ${fieldName}`}
        </button>
        <span style={styles.uploadHint}>PDF, Word, PowerPoint, or .txt</span>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.pptx,.txt"
          onChange={handleFile}
          style={{ display: 'none' }}
        />
      </div>

      {showPreview && (
        <div style={styles.previewCard}>
          <div style={styles.previewHeader}>
            <p style={styles.previewTitle}>📄 {filename}</p>
            {redactedCount > 0 && (
              <p style={styles.redactedBadge}>{redactedCount} items auto-redacted</p>
            )}
          </div>

          <div style={styles.previewText}>
            {redactedText.substring(0, 500)}
            {redactedText.length > 500 && <span style={{ color: '#334155' }}>... ({redactedText.length} chars total)</span>}
          </div>

          <div style={styles.disclaimerBox}>
            <p style={styles.disclaimerTitle}>⚠ REVIEW REQUIRED</p>
            <p style={styles.disclaimerText}>
              Our auto-redaction detects financial figures, emails, phone numbers, and dates.
              It may not catch all sensitive content. Always review before proceeding.
            </p>
          </div>

          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              style={styles.checkbox}
            />
            <span style={styles.checkboxLabel}>
              I have reviewed the redacted content and confirm no sensitive information remains.
            </span>
          </label>

          <div style={styles.previewButtons}>
            <button
              type="button"
              style={styles.confirmButton}
              onClick={handleConfirm}
              disabled={!confirmed}
            >
              Use This Content
            </button>
            <button
              type="button"
              style={styles.discardButton}
              onClick={handleDiscard}
            >
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '10px' },
  uploadRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  uploadButton: { backgroundColor: '#1E293B', color: '#3B82F6', border: '1px solid #3B82F6', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  uploadHint: { color: '#334155', fontSize: '12px' },
  previewCard: { backgroundColor: '#0A1628', borderRadius: '12px', padding: '16px', border: '1px solid #1E3A5F', display: 'flex', flexDirection: 'column', gap: '12px' },
  previewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  previewTitle: { color: '#3B82F6', fontSize: '13px', fontWeight: '600', margin: 0 },
  redactedBadge: { backgroundColor: '#1A1200', color: '#F59E0B', fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '4px', border: '1px solid #F59E0B', margin: 0 },
  previewText: { color: '#475569', fontSize: '12px', lineHeight: '1.6', backgroundColor: '#0F172A', borderRadius: '8px', padding: '12px', maxHeight: '120px', overflowY: 'auto', fontFamily: 'monospace' },
  disclaimerBox: { backgroundColor: '#0F172A', borderRadius: '8px', padding: '12px', border: '1px solid #1E293B' },
  disclaimerTitle: { color: '#F59E0B', fontSize: '10px', fontWeight: '700', letterSpacing: '2px', margin: '0 0 4px 0' },
  disclaimerText: { color: '#475569', fontSize: '12px', lineHeight: '1.5', margin: 0 },
  checkboxRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' },
  checkbox: { width: '16px', height: '16px', accentColor: '#3B82F6', flexShrink: 0, marginTop: '2px', cursor: 'pointer' },
  checkboxLabel: { color: '#94A3B8', fontSize: '13px', lineHeight: '1.5' },
  previewButtons: { display: 'flex', gap: '8px' },
  confirmButton: { flex: 1, backgroundColor: '#3B82F6', color: '#fff', padding: '12px', borderRadius: '8px', fontSize: '14px', fontWeight: '700', border: 'none', cursor: 'pointer' },
  discardButton: { backgroundColor: 'transparent', color: '#475569', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer' },
};